import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { advisorHeaders } from "../../lib/clarity";

const KEYS = [["cash", "Cash Flow & Savings"], ["tax", "Tax Efficiency"], ["protection", "Insurance & Protection"], ["networth", "Net Worth & Resilience"], ["retirement", "Retirement Readiness"]];

export function WeightsEditor() {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/admin/clarity-settings/weights", advisorHeaders()).then((r) => setW(r.data.weights)).catch(() => {}); }, []);
  if (!w) return <p className="text-exodus-muted">Loading…</p>;
  const total = KEYS.reduce((s, [k]) => s + (Number(w[k]) || 0), 0);
  const save = async () => {
    try {
      await api.put("/admin/clarity-settings/weights", Object.fromEntries(KEYS.map(([k]) => [k, Number(w[k])])), advisorHeaders());
      toast.success("Weights saved. New assessments use these weights.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save weights."); }
  };
  return (
    <div data-testid="admin-clarity-weights" className="max-w-xl space-y-4">
      <p className="text-sm text-exodus-muted">Category weights must total 100. Changes apply to new and re-scored assessments; existing reports keep the weights they were scored with.</p>
      {KEYS.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between gap-4 border-b border-exodus-border pb-3">
          <span className="text-sm">{l}</span>
          <input data-testid={`weight-${k}`} type="number" min={0} max={100} className="input-exodus !w-24 text-right" value={w[k]} onChange={(e) => setW({ ...w, [k]: e.target.value })} />
        </label>
      ))}
      <p data-testid="weights-total" className={`text-sm ${total === 100 ? "text-exodus-emerald" : "text-orange-300"}`}>Total: {total}</p>
      <button data-testid="weights-save" onClick={save} disabled={total !== 100} className="btn-gold">Save weights</button>
    </div>
  );
}

export function Funnel({ stats }) {
  const ev = stats.events;
  const top = [["page_view", "Page visits"], ["start", "Assessment starts"], ["complete", "Quick completions"], ["contact_submit", "Contact shared"], ["skip_contact", "Skipped contact"],
    ["report_view", "Report views"], ["deep_start", "Deeper started"], ["deep_complete", "Deeper completed"], ["pdf_download", "PDF downloads"], ["print", "Prints"],
    ["book_click", "Book-a-call clicks"], ["callback_request", "Callback requests"], ["tax_buckets_view", "Tax Buckets visits"], ["illustrator_run", "Illustrator runs"]];
  const max = Math.max(1, ...stats.steps.map((s) => s.sessions));
  return (
    <div data-testid="admin-clarity-funnel" className="grid lg:grid-cols-2 gap-8">
      <div>
        <p className="eyebrow mb-4">Journey events (unique sessions)</p>
        <div className="grid grid-cols-2 gap-3">
          {top.map(([k, l]) => <div key={k} className="card-surface p-4"><p className="text-[10px] eyebrow text-exodus-muted">{l}</p><p className="font-display text-3xl gold-text">{ev[k] || 0}</p></div>)}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-4">Question-level drop-off (sessions reaching each step)</p>
        <div className="space-y-2">
          {stats.steps.length === 0 && <p className="text-exodus-muted text-sm">No step data yet.</p>}
          {stats.steps.map((s) => (
            <div key={s.step} className="text-xs">
              <div className="flex justify-between text-exodus-muted"><span className="truncate pr-3">{s.tier === "deep" ? "◆ " : ""}{s.prompt}</span><span className="text-exodus-text">{s.sessions}</span></div>
              <div className="h-1.5 bg-exodus-border mt-1"><div className="h-1.5 bg-exodus-gold" style={{ width: `${(s.sessions / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
